import ky, { HTTPError } from 'ky';
import type {
  PauseJobResponse,
  ResumeJobResponse,
  StopJobResponse,
  RestartJobResponse,
  CheckpointsResponse,
  CheckpointId,
} from '@/types/api';

/**
 * API Configuration
 * VITE_API_URL should be provided per environment at build/deploy time.
 * We intentionally avoid any hardcoded staging fallback.
 */
const configuredApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
export const API_URL = configuredApiUrl || (import.meta.env.DEV ? 'http://localhost:8787' : window.location.origin);

/**
 * Base URL for book cover images served by the Distribution worker.
 *
 * Expected to point at the public covers proxy, for example:
 *   https://distribution-api.kampus.io/public/covers
 *   https://kampus_distribution_worker.<account>.workers.dev/public/covers
 *
 * When not configured, the UI will fall back to generated placeholders.
 */
export const COVERS_BASE_URL = (import.meta.env.VITE_COVERS_BASE_URL as string | undefined) || '';

/**
 * Custom API error class with structured error information
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly errorCode?: string;
  public readonly details?: unknown;

  constructor(
    message: string,
    status: number,
    statusText: string,
    errorCode?: string,
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.errorCode = errorCode;
    this.details = details;
  }

  /**
   * Check if error is an authentication error
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if error is a forbidden error
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if error is a not found error
   */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if error is a server error
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Check if error is a validation error
   */
  isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<{ error: string; code?: string; details?: unknown }> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const data = await response.json() as { error?: string; message?: string; code?: string; details?: unknown };
      return {
        error: data.error || data.message || response.statusText,
        code: data.code,
        details: data.details,
      };
    }
    const text = await response.text();
    return { error: text || response.statusText };
  } catch {
    return { error: response.statusText };
  }
}

/**
 * Token refresh state to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh the auth token
 */
async function refreshAuthToken(): Promise<boolean> {
  // If already refreshing, wait for the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const token = localStorage.getItem('auth-token');
      if (!token) return false;

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return false;

      const data = await response.json() as { token?: string; ok?: boolean };
      if (data.token) {
        localStorage.setItem('auth-token', data.token);
        return true;
      }

      return data.ok ?? false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Configured ky instance for API requests
 * - Automatically includes auth token from localStorage
 * - Sets JSON content type
 * - Configures timeout and retry behavior
 * - Enhanced error handling
 */
export const api = ky.create({
  prefixUrl: API_URL,
  timeout: 30000,
  retry: {
    limit: 2,
    methods: ['get'],
    statusCodes: [408, 429, 502, 503, 504],
    // Don't retry on 500 as it might be a real error
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = localStorage.getItem('auth-token');
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    beforeRetry: [
      async ({ request, error, retryCount }) => {
        console.log(`[API] Retrying request (attempt ${retryCount + 1}):`, request.url, error);
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        // Handle 401 unauthorized
        if (response.status === 401) {
          // Check if this is the login endpoint - don't try to refresh
          if (request.url.includes('/auth/login')) {
            return response;
          }

          // Try to refresh the token
          const refreshed = await refreshAuthToken();
          
          if (refreshed) {
            // Retry the original request with new token
            const newToken = localStorage.getItem('auth-token');
            if (newToken) {
              request.headers.set('Authorization', `Bearer ${newToken}`);
              return ky(request);
            }
          }

          // Refresh failed, clear auth and redirect to login
          localStorage.removeItem('auth-token');
          localStorage.removeItem('refresh-token');
          localStorage.removeItem('auth-storage');
          
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error;
        if (response) {
          const { error: errorMessage, code, details } = await parseErrorResponse(response.clone());
          
          return new ApiError(
            errorMessage,
            response.status,
            response.statusText,
            code,
            details
          ) as unknown as HTTPError;
        }
        return error;
      },
    ],
  },
});

/**
 * Type-safe API request helpers with enhanced error handling
 */
export const apiClient = {
  /**
   * GET request
   */
  get: async <T>(path: string, options?: Parameters<typeof api.get>[1]): Promise<T> => {
    try {
      return await api.get(path, options).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },

  /**
   * POST request
   */
  post: async <T>(path: string, data?: unknown, options?: Parameters<typeof api.post>[1]): Promise<T> => {
    try {
      return await api.post(path, { json: data, ...options }).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },

  /**
   * POST multipart/form-data (e.g. file uploads)
   */
  postForm: async <T>(
    path: string,
    formData: FormData,
    options?: Parameters<typeof api.post>[1]
  ): Promise<T> => {
    try {
      // Important: do NOT set Content-Type; ky will add the correct boundary.
      return await api.post(path, { body: formData, ...options }).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },

  /**
   * PUT request
   */
  put: async <T>(path: string, data?: unknown, options?: Parameters<typeof api.put>[1]): Promise<T> => {
    try {
      return await api.put(path, { json: data, ...options }).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },

  /**
   * PATCH request
   */
  patch: async <T>(path: string, data?: unknown, options?: Parameters<typeof api.patch>[1]): Promise<T> => {
    try {
      return await api.patch(path, { json: data, ...options }).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },

  /**
   * DELETE request
   */
  delete: async <T>(path: string, options?: Parameters<typeof api.delete>[1]): Promise<T> => {
    try {
      return await api.delete(path, options).json<T>();
    } catch (error) {
      throw normalizeError(error);
    }
  },
};

/**
 * Normalize any error to ApiError
 */
function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof HTTPError) {
    return new ApiError(
      error.message,
      error.response.status,
      error.response.statusText
    );
  }

  if (error instanceof Error) {
    // Network error or other error
    return new ApiError(
      error.message,
      0,
      'Network Error',
      'NETWORK_ERROR'
    );
  }

  return new ApiError(
    'An unexpected error occurred',
    0,
    'Unknown Error',
    'UNKNOWN_ERROR'
  );
}

/**
 * Helper to check if an error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Helper to get error message from any error
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.details && typeof error.details === 'object' && !Array.isArray(error.details)) {
      const fieldErrors = error.details as Record<string, string[] | undefined>;
      const parts = Object.entries(fieldErrors)
        .filter(([, msgs]) => msgs && msgs.length > 0)
        .map(([field, msgs]) => `${field}: ${msgs!.join(', ')}`);
      if (parts.length > 0) {
        return `${error.message} (${parts.join('; ')})`;
      }
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// ============================================
// TTS Job Control APIs (Pause/Resume/Stop)
// ============================================

/**
 * TTS Job Control API functions
 */
export const ttsJobApi = {
  /**
   * Request to pause a job at the next checkpoint
   * @param jobId - The job ID to pause
   * @param checkpoint - Optional specific checkpoint to pause at
   */
  pause: async (jobId: string, checkpoint?: CheckpointId): Promise<PauseJobResponse> => {
    return apiClient.post<PauseJobResponse>(`tts/jobs/${jobId}/pause`, { checkpoint });
  },

  /**
   * Resume a paused job
   * @param jobId - The job ID to resume
   * @param skipToCheckpoint - Optional checkpoint to skip to
   */
  resume: async (jobId: string, skipToCheckpoint?: CheckpointId): Promise<ResumeJobResponse> => {
    return apiClient.post<ResumeJobResponse>(`tts/jobs/${jobId}/resume`, { skip_to_checkpoint: skipToCheckpoint });
  },

  /**
   * Permanently stop a job (cannot be resumed)
   * @param jobId - The job ID to stop
   * @param reason - Optional reason for stopping
   */
  stop: async (jobId: string, reason?: string): Promise<StopJobResponse> => {
    return apiClient.post<StopJobResponse>(`tts/jobs/${jobId}/stop`, { reason });
  },

  /**
   * Get available checkpoints for a job
   * @param jobId - The job ID to get checkpoints for
   */
  getCheckpoints: async (jobId: string): Promise<CheckpointsResponse> => {
    return apiClient.get<CheckpointsResponse>(`tts/jobs/${jobId}/checkpoints`);
  },

  /**
   * Delete a stopped job
   * @param jobId - The job ID to delete
   * @param deleteFiles - Whether to delete associated files (default: true)
   */
  delete: async (jobId: string, deleteFiles: boolean = true): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(
      `tts/jobs/${jobId}?delete_files=${deleteFiles ? 1 : 0}`
    );
  },

  /**
   * Restart a failed/stopped/completed job from scratch
   * This will clean up all artifacts and re-trigger the pipeline
   * @param jobId - The job ID to restart
   */
  restart: async (jobId: string): Promise<RestartJobResponse> => {
    return apiClient.post<RestartJobResponse>(`tts/jobs/${jobId}/restart`, {});
  },
};

export default apiClient;
